import {
  CAMPAIGN_BIDS,
  DELETE_CAMPAIGN_BIDS,
  ADD_CAMPAIGN_BID,
  EDIT_CAMPAIGN_BIDS,
  CHANGE_SINGLE_BID_STATUS,
  CHANGE_BIDS_STATUS,
  RESET,
  EDIT_SINGLE_CAMPAIGN_BID,
  SELECTED_CAMPAIGN_BIDS,
  CHANGE_BID_MATCHING_TYPE,
  CAMPAIGN_BIDS_META_DATA,
  SEARCH_ADS_CAMPAIGN_HINTS_SET_KEYWORDS_HINT,
} from '../../types';

import { AdBid } from '@/api/api';

import subtractArrays from '@/utils/subtractArrays';
import {
  convertArrayOfObjectToHashTable,
  convertArrayOfPrimitiveToHashTable,
} from '@/utils/arrayToHashTable';

export const state = () => ({
  bids: [],
  metadata: {},
});

export const getters = {
  [CAMPAIGN_BIDS]({ bids }) {
    return bids;
  },
  [CAMPAIGN_BIDS_META_DATA]({ metadata }) {
    return metadata;
  },
  [SELECTED_CAMPAIGN_BIDS]({ bids }) {
    return ids => {
      const idsHashTable = convertArrayOfPrimitiveToHashTable(ids);
      return bids.filter(({ id: bidId }) => bidId in idsHashTable);
    };
  },
};

/* eslint-disable no-param-reassign */
export const mutations = {
  [CAMPAIGN_BIDS](stateObj, bids) {
    stateObj.bids = bids;
  },
  [CAMPAIGN_BIDS_META_DATA](stateObj, metadata) {
    stateObj.metadata = metadata;
  },
  [EDIT_CAMPAIGN_BIDS](stateObj, bidInfos) {
    const bidInfosHashTable = convertArrayOfObjectToHashTable(
      bidInfos,
      ({ id }) => id,
    );
    stateObj.bids = stateObj.bids.map(item => {
      if (item.id in bidInfosHashTable) {
        return {
          ...item,
          ...bidInfosHashTable[item.id],
        };
      }
      return item;
    });
  },
  [EDIT_SINGLE_CAMPAIGN_BID](stateObj, bidInfo) {
    stateObj.bids = stateObj.bids.map(item => {
      const isChanged = bidInfo.id === item.id;
      if (isChanged) {
        return {
          ...item,
          ...bidInfo,
        };
      }
      return item;
    });
  },
  [ADD_CAMPAIGN_BID](stateObj, bid) {
    stateObj.bids.unshift(bid);
  },
  [DELETE_CAMPAIGN_BIDS](stateObj, ids) {
    stateObj.bids = subtractArrays(stateObj.bids, ids, 'id');
  },
  [CHANGE_BIDS_STATUS](stateObj, { ids, paused }) {
    const idsHashTable = convertArrayOfPrimitiveToHashTable(ids);
    stateObj.bids = stateObj.bids.map(bid => {
      const isChanged = bid.id in idsHashTable;
      if (isChanged) {
        return {
          ...bid,
          paused,
        };
      }
      return bid;
    });
  },
  [CHANGE_SINGLE_BID_STATUS](stateObj, { id, paused }) {
    stateObj.bids = stateObj.bids.map(bid => {
      const isChanged = id === bid.id;
      if (isChanged) {
        return {
          ...bid,
          paused,
        };
      }
      return bid;
    });
  },
  [CHANGE_BID_MATCHING_TYPE](stateObj, { id, matchingType }) {
    stateObj.bids = stateObj.bids.map(bid => {
      const isChanged = id === bid.id;
      if (isChanged) {
        return {
          ...bid,
          matchingType,
        };
      }
      return bid;
    });
  },
  [RESET](stateObj) {
    Object.assign(stateObj, state());
  },
};

export const actions = {
  [CAMPAIGN_BIDS]({ commit }, bidInfo) {
    return AdBid.getBids(bidInfo).then(({ bids, metadata }) => {
      commit(CAMPAIGN_BIDS, bids);
      commit(CAMPAIGN_BIDS_META_DATA, metadata);
      return {
        bids,
        metadata,
      };
    });
  },
  /**
   * @typedef {Object} bidInfo
   * @property {number} id
   * @property {string} matchingType
   * @property {number} priceOffer
   *
   * @param {Object} bidData
   * @param {string} bidData.campaignId
   * @param {Array<bidInfo>} bidData.bidsInfo
   */
  [EDIT_CAMPAIGN_BIDS]({ commit }, { campaignId, bidsInfo }) {
    return AdBid.editBids({ campaignId, bidsInfo }).then(message => {
      commit(EDIT_CAMPAIGN_BIDS, bidsInfo);
      return message;
    });
  },
  /**
   * @param {Object} bidData
   * @param {string} bidData.campaignId
   * @param {bidInfo} bidData.bidInfo
   */
  [EDIT_SINGLE_CAMPAIGN_BID]({ commit }, { campaignId, bidInfo }) {
    return AdBid.editBid(campaignId, bidInfo).then(message => {
      commit(EDIT_SINGLE_CAMPAIGN_BID, bidInfo);
      return message;
    });
  },
  [ADD_CAMPAIGN_BID]({ commit, dispatch }, { campaignId, bid }) {
    return AdBid.addBid({
      campaignId,
      ...bid,
    }).then(({ data, message }) => {
      dispatch(SEARCH_ADS_CAMPAIGN_HINTS_SET_KEYWORDS_HINT, false);

      commit(ADD_CAMPAIGN_BID, data);
      return message;
    });
  },
  [CHANGE_SINGLE_BID_STATUS]({ commit }, { paused, id, campaignId }) {
    return AdBid.changeSingleBidStatus({
      id,
      campaignId,
      paused,
    }).then(message => {
      commit(CHANGE_SINGLE_BID_STATUS, { id, paused });
      return message;
    });
  },
  [CHANGE_BIDS_STATUS]({ commit }, { ids, campaignId, paused }) {
    return AdBid.changeBidsStatus({
      ids,
      campaignId,
      paused,
    }).then(message => {
      commit(CHANGE_BIDS_STATUS, { ids, paused });
      return message;
    });
  },
  [CHANGE_BID_MATCHING_TYPE]({ commit }, { id, campaignId, matchingType }) {
    return AdBid.changeMatchingType({
      id,
      campaignId,
      matchingType,
    }).then(message => {
      commit(CHANGE_BID_MATCHING_TYPE, { id, matchingType });
      return message;
    });
  },
  [DELETE_CAMPAIGN_BIDS]({ commit, getters, dispatch }, { ids, campaignId }) {
    return AdBid.deleteBids({
      ids,
      campaignId,
    })
      .then(message => {
        commit(DELETE_CAMPAIGN_BIDS, ids);
        return message;
      })
      .then(message => {
        if (!getters[CAMPAIGN_BIDS].length)
          dispatch(SEARCH_ADS_CAMPAIGN_HINTS_SET_KEYWORDS_HINT, true);

        return message;
      });
  },
};

export default {
  state,
  getters,
  mutations,
  actions,
};
